import type { PortalOrderDetailResponse } from '../../services/portalOrders';

/**
 * Boundary check for an order-detail payload before it enters modal state.
 *
 * `PortalOrderDetailResponse.order` is typed as required, and the modal's
 * render body dereferences `orderDetail.order.*` in dozens of places. A
 * payload that is truthy but lacks `.order` (a half-serialized response, a
 * proxy error body that slipped through with a 200, a future API change)
 * would otherwise crash the whole modal with
 * "Cannot read properties of undefined (reading '...')". Validating once
 * here lets the modal show its "Order Not Available" state instead.
 */
export function hasOrderRecord(
  detail: PortalOrderDetailResponse | null | undefined,
): detail is PortalOrderDetailResponse {
  if (!detail || typeof detail !== 'object') return false;
  const order = (detail as { order?: unknown }).order;
  return typeof order === 'object' && order !== null;
}

/** Error copy shown when the server returned a detail payload without `.order`. */
export const MISSING_ORDER_RECORD_MESSAGE =
  'The server returned this order without its order record. Please refresh or contact support.';

// ─── Quote attachment (Open Quotes tab) ─────────────────────────────────────

/** The five Sage UDF_QUOTE slots an order can carry, in fill order. */
export const QUOTE_SLOTS = [
  'udf_quote',
  'udf_quote_2',
  'udf_quote_3',
  'udf_quote_4',
  'udf_quote_5',
] as const;

export type QuoteSlot = (typeof QUOTE_SLOTS)[number];

/** The subset of an order record the quote-slot helpers read. */
export type QuoteSlotFields = { [K in QuoteSlot]?: string | null };

/**
 * Canonical form of a quote reference for equality checks.
 *
 * The backend stamps `str(source_id)` into the UDF_QUOTE slots
 * (services/orders/quote_reference_stamper.py) while the modal's one-click
 * Attach writes the id zero-padded to 5 digits — so "9821" and "09821"
 * must compare equal or the "Attached" state and the duplicate-attach
 * guard disagree. Numeric refs drop leading zeros; anything else (a PO
 * number a CSR keyed into the slot) is compared trimmed and as-is.
 * Returns null for an empty slot.
 */
export function normalizeQuoteRef(value: string | number | null | undefined): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) return text.replace(/^0+(?=\d)/, '');
  return text;
}

/** Normalized refs of every quote already on the order's UDF_QUOTE slots. */
export function getAttachedQuoteRefs(order: QuoteSlotFields): Set<string> {
  const refs = new Set<string>();
  for (const slot of QUOTE_SLOTS) {
    const ref = normalizeQuoteRef(order[slot]);
    if (ref) refs.add(ref);
  }
  return refs;
}

/** The slot holding `quoteRef` (padding-insensitive), or null when absent. */
export function findQuoteSlot(
  order: QuoteSlotFields,
  quoteRef: string | number,
): QuoteSlot | null {
  const ref = normalizeQuoteRef(quoteRef);
  if (ref == null) return null;
  return QUOTE_SLOTS.find((slot) => normalizeQuoteRef(order[slot]) === ref) ?? null;
}

/** True when `quoteSourceId` already occupies one of the order's slots. */
export function isQuoteAttached(
  order: QuoteSlotFields,
  quoteSourceId: string | number,
): boolean {
  return findQuoteSlot(order, quoteSourceId) != null;
}

/** First empty UDF_QUOTE slot, or null when all five are used. */
export function findEmptyQuoteSlot(order: QuoteSlotFields): QuoteSlot | null {
  return QUOTE_SLOTS.find((slot) => normalizeQuoteRef(order[slot]) == null) ?? null;
}
